import { getPatientCartsQuery } from '@/api/carts/cartQueries';
import { currentUserQuery } from '@/api/user/userQueries';
import { getAllUsersQuery } from '@/api/users/usersQueries';
import type { ComboboxItem } from '@/components/common/Combobox';
import { Combobox } from '@/components/common/Combobox';
import { TableCell, TableHead } from '@/components/common/Table';
import type { Cart } from '@/domain/Cart';
import { Role } from '@/domain/Role';
import { useHasRole } from '@/hooks/useHasRole';
import { formatDate } from '@/lib/utils/dateUtils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';

function CartsTableContent({
  carts,
  isLoading,
  emptyMessage,
  accordionOpenValue,
  filtersComponent,
  paginationMeta,
  onPageChange,
}: {
  carts: Array<Cart>;
  isLoading: boolean;
  emptyMessage: string;
  accordionOpenValue?: string;
  filtersComponent?: React.ReactNode;
  paginationMeta?: {
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
  };
  onPageChange?: (page: number) => void;
}) {
  return (
    <DashboardTableAccordion
      value="patient-carts"
      title="Waiting carts"
      accordionOpenValue={accordionOpenValue}
      data={carts}
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      loadingMessage="Loading patient carts..."
      paginationMeta={paginationMeta}
      onPageChange={onPageChange}
      filtersComponent={filtersComponent}
      renderHeader={() => (
        <>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Created date</TableHead>
          <TableHead>Cart name</TableHead>
        </>
      )}
      renderRow={(cart: Cart) => (
        <>
          <TableCell>
            {cart.user
              ? `${cart.user.name} ${cart.user.surname}`
              : `User #${cart.user_id}`}
          </TableCell>
          <TableCell>{cart.user?.email ?? '-'}</TableCell>
          <TableCell>{formatDate(cart.created_at)}</TableCell>
          <TableCell>{cart.name}</TableCell>
        </>
      )}
    />
  );
}

function DoctorPatientCartsContent({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery(getPatientCartsQuery(undefined, page));
  const carts = data?.data ?? [];

  return (
    <CartsTableContent
      carts={carts}
      isLoading={isLoading}
      emptyMessage="No waiting carts found for your patients."
      accordionOpenValue={accordionOpenValue}
      paginationMeta={data?.meta}
      onPageChange={setPage}
    />
  );
}

function AdminPatientCartsContent({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) {
  const [selectedDoctorId, setSelectedDoctorId] = useState<
    number | undefined
  >();
  const [selfCarts, setSelfCarts] = useState(false);
  const [page, setPage] = useState(1);
  const { data: currentUserData } = useQuery({
    ...currentUserQuery,
    retry: false,
  });

  const { data: doctorsData, isLoading: doctorsLoading } = useQuery(
    getAllUsersQuery({ role: 'Doctor' }),
  );

  const doctorItems: Array<ComboboxItem> = useMemo(
    () =>
      (doctorsData ?? []).map((doctor) => ({
        id: doctor.id,
        value: String(doctor.id),
        label: `${doctor.name} ${doctor.surname}`,
      })),
    [doctorsData],
  );

  const effectiveDoctorId = selfCarts
    ? currentUserData?.data.id
    : selectedDoctorId;

  const { data: cartsData, isLoading: cartsLoading } = useQuery({
    ...getPatientCartsQuery(effectiveDoctorId, page),
    enabled: effectiveDoctorId !== undefined,
  });

  useEffect(() => {
    if (accordionOpenValue === 'patient-carts') {
      setSelectedDoctorId(undefined);
      setSelfCarts(false);
      setPage(1);
    }
  }, [accordionOpenValue]);

  const hasDoctorSelected = effectiveDoctorId !== undefined;
  const carts = hasDoctorSelected ? (cartsData?.data ?? []) : [];

  const handleDoctorChange = (val: string | undefined) => {
    if (val !== undefined) {
      setSelfCarts(false);
    }
    setSelectedDoctorId(val !== undefined ? Number(val) : undefined);
    setPage(1);
  };

  const filtersComponent = (
    <div className="flex max-w-lg items-center gap-4 pt-3">
      <Combobox
        items={doctorItems}
        value={selfCarts ? undefined : selectedDoctorId}
        onChange={handleDoctorChange}
        label={doctorsLoading ? 'Loading doctors...' : 'Select a doctor...'}
        disabled={doctorsLoading}
      />
      <label className="text-table-foreground flex items-center gap-2 text-base whitespace-nowrap">
        <input
          type="checkbox"
          checked={selfCarts}
          onChange={(e) => {
            const checked = e.target.checked;
            setSelfCarts(checked);
            if (checked) {
              setSelectedDoctorId(undefined);
            }
            setPage(1);
          }}
        />
        Admin created carts
      </label>
    </div>
  );

  return (
    <CartsTableContent
      carts={carts}
      isLoading={cartsLoading && hasDoctorSelected}
      emptyMessage={
        !hasDoctorSelected
          ? 'Select a doctor to view their patients\u2019 waiting carts.'
          : 'No waiting carts found for this doctor\u2019s patients.'
      }
      accordionOpenValue={accordionOpenValue}
      filtersComponent={filtersComponent}
      paginationMeta={hasDoctorSelected ? cartsData?.meta : undefined}
      onPageChange={setPage}
    />
  );
}

export const PatientCartsRow = ({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) => {
  const { hasRole } = useHasRole();
  const isAdmin = hasRole(Role.SHOP_ADMIN, Role.SUPERADMIN);

  if (isAdmin) {
    return <AdminPatientCartsContent accordionOpenValue={accordionOpenValue} />;
  }

  return <DoctorPatientCartsContent accordionOpenValue={accordionOpenValue} />;
};
